/**
 * RightPanelRegistryContext
 *
 * Pages register additional sidebar tabs here (e.g. TOC, Comments, Related).
 * GlobalChatSidebar reads these and merges them with the always-present Chat tab.
 * When the page unmounts, tabs unregister automatically.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

export interface PanelTabRegistration {
  id: string;
  icon: ReactNode;
  title: string;
  badge?: boolean;
  badgeColor?: 'coral' | 'amber';
  render: () => ReactNode;
}

export interface RightPanelRegistryContextValue {
  pageTabs: PanelTabRegistration[];
  registerTabs: (tabs: PanelTabRegistration[]) => void;
  unregisterTabs: () => void;
}

const RightPanelRegistryContext = createContext<RightPanelRegistryContextValue | undefined>(undefined);

export function RightPanelRegistryProvider({ children }: { children: ReactNode }) {
  const [pageTabs, setPageTabs] = useState<PanelTabRegistration[]>([]);

  // registerTabs must replace state unconditionally: tabs typically include
  // `render` closures that capture the caller's current props/state, and
  // dropping updates because the structural shape (id/title/badge) didn't
  // change leaves consumers rendering stale content. The feedback-loop
  // concern is addressed upstream — hooks like useDocumentLinks /
  // useDocumentComments now return stable references when their data hasn't
  // changed, so this useEffect dep chain no longer fires every render.
  const registerTabs = useCallback((tabs: PanelTabRegistration[]) => {
    setPageTabs(tabs);
  }, []);

  const unregisterTabs = useCallback(() => {
    setPageTabs((prev) => (prev.length === 0 ? prev : []));
  }, []);

  // Memoize the value — without this, every render of this provider creates
  // a fresh object and every consumer re-renders, cascading through every
  // tab's render subtree.
  const value = useMemo<RightPanelRegistryContextValue>(
    () => ({ pageTabs, registerTabs, unregisterTabs }),
    [pageTabs, registerTabs, unregisterTabs],
  );

  return (
    <RightPanelRegistryContext.Provider value={value}>
      {children}
    </RightPanelRegistryContext.Provider>
  );
}

export function useRightPanelRegistry(): RightPanelRegistryContextValue {
  const context = useContext(RightPanelRegistryContext);
  if (!context) {
    throw new Error('useRightPanelRegistry must be used within a RightPanelRegistryProvider');
  }
  return context;
}
